import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
} from "@mui/material";
import { useGetSitesQuery } from "../../../features/sites/sitesApiSlice";
import {
  defaultBookMonth,
  monthOptions,
} from "../../../components/sepa/SepaUtils";

// Simple MM/YYYY guard (allow empty for "clear")
const isMmYyyy = (s) => /^(\d{2})\/(\d{4})$/.test(s);

export default function ResetPayedTillDialog({ open, onClose, onConfirm }) {
  const { data: sites = [] } = useGetSitesQuery();

  const [scope, setScope] = useState("ALL"); // "ALL" | "SITES"
  const [selectedSiteIds, setSelectedSiteIds] = useState([]);
  const [onlyActive, setOnlyActive] = useState(true);
  const [setTo, setSetTo] = useState(defaultBookMonth(-1));

  const canSubmit = useMemo(() => {
    if (scope === "SITES" && selectedSiteIds.length === 0) return false;
    if (setTo && !isMmYyyy(setTo)) return false;
    return true;
  }, [scope, selectedSiteIds, setTo]);

  const handleSubmit = () => {
    onConfirm({
      scope,
      siteIds: selectedSiteIds,
      onlyActive,
      setTo: setTo || null,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Zahlstatus zurücksetzen</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel id="scope-label">Bereich</InputLabel>
            <Select
              labelId="scope-label"
              label="Bereich"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
            >
              <MenuItem value="ALL">Alle Mitglieder</MenuItem>
              <MenuItem value="SITES">Ausgewählte Standorte</MenuItem>
            </Select>
          </FormControl>

          {scope === "SITES" && (
            <FormControl fullWidth>
              <InputLabel id="sites-label">Standorte</InputLabel>
              <Select
                multiple
                labelId="sites-label"
                label="Standorte"
                value={selectedSiteIds}
                onChange={(e) => setSelectedSiteIds(e.target.value)}
                input={<OutlinedInput label="Standorte" />}
                renderValue={(selected) => {
                  const map = new Map(
                    sites.map((s) => [String(s._id || s.id), s])
                  );
                  return selected
                    .map((id) => map.get(String(id))?.siteName || "—")
                    .join(", ");
                }}
              >
                {sites.map((s) => {
                  const id = String(s._id || s.id);
                  return (
                    <MenuItem key={id} value={id}>
                      <Checkbox checked={selectedSiteIds.indexOf(id) > -1} />
                      {s.siteName}
                    </MenuItem>
                  );
                })}
              </Select>
              {selectedSiteIds.length === 0 && (
                <FormHelperText>
                  Wählen Sie mindestens einen Standort.
                </FormHelperText>
              )}
            </FormControl>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={onlyActive}
                onChange={(e) => setOnlyActive(e.target.checked)}
              />
            }
            label="Nur aktive Mitglieder berücksichtigen"
          />
          <div>
            <FormControl fullWidth>
              <InputLabel id="bm-label">Zurücksetzen auf</InputLabel>
              <Select
                labelId="bm-label"
                label="Zurücksetzen auf"
                value={setTo}
                onChange={(e) => setSetTo(e.target.value)}
              >
                {monthOptions().map((m) => (
                  <MenuItem key={m.id} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="warning"
          disabled={!canSubmit}
        >
          Zurücksetzen
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ResetPayedTillDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};
